import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { CommonTranslationKeys } from 'intl/keys';
import { useDocumentationBrowseDialog } from 'components/common/withDocumentationBrowseDialog';
import {
  DocumentationBrowserDialog,
  getDocumentationLocalIdFromRowId,
  useDocumentationBrowserState,
} from '../DocumentationBrowser';
import { Store } from 'hooks/createStore';
import { SetValuesOptions } from 'components/dataCreationForms/withInputForm';
import type { LevelMapReference } from 'shared/domain/level/types/model';
import { normalizeDirectoryId } from 'shared/domain/directory/directoryIds';

export type SetValuesContext = () => SetValues;
interface SetValues {
  setValues: (
    key: string,
    value: LevelMapReference | undefined,
    options?: SetValuesOptions  
  ) => void;
}

export interface DocumentationBrowseDialogProps {
  setValuesContext: SetValuesContext;
  formKey: string;
  setFilename: (name: string) => void;
  selectedStore: Store<string | undefined>;
  title: { name: string } | undefined;
  initialSelect: string | undefined;
  initialDirectory: string;
  /**
   * Directory of the selected file (or the initial one after cancel). The dialog
   * does not change the documentation grid's current directory.
   */
  onDirectoryChange?: (directoryId: string) => void;
}

export function DocumentationBrowseDialog({
  setValuesContext,
  formKey,
  setFilename,
  selectedStore,
  title,
  initialSelect,
  initialDirectory,
  onDirectoryChange,
}: DocumentationBrowseDialogProps): ReactElement {
  const { setValues } = setValuesContext();
  const { isOpenStore, closeDialog } = useDocumentationBrowseDialog();
  const [isOpen, setIsOpen] = useState(isOpenStore.get());
  const initialDocumentationLocalId = useMemo(
    () =>
      getDocumentationLocalIdFromRowId(initialSelect ?? '') ?? undefined,
    [initialSelect]
  );

  const browser = useDocumentationBrowserState({
    isOpen,
    selectableDocumentation: true,
    singleDocumentationSelection: true,
    initialDirectoryId: initialDirectory || undefined,
    initialDirectoryName: title?.name,
    initialDocumentationLocalId,
  });

  // Any documentation may be a level's map (PT-7117, D3): one that cannot be drawn is warned about where the map is shown.
  const selectedDocumentation = browser.selectedDocumentation;
  const enableSelect = Boolean(selectedDocumentation);

  useEffect(() => {
    setIsOpen(isOpenStore.get());
    return isOpenStore.subscribe(() => {
      setIsOpen(isOpenStore.get());
    });
  }, [isOpenStore]);

  const onSubmit = useCallback(() => {
    if (!selectedDocumentation) {
      return;
    }

    setValues(formKey, { documentationId: selectedDocumentation._id });

    setFilename(selectedDocumentation.name);
    onDirectoryChange?.(normalizeDirectoryId(selectedDocumentation.parentId));
    closeDialog();
  }, [
    selectedDocumentation,
    setValues,
    formKey,
    setFilename,
    onDirectoryChange,
    closeDialog,
  ]);

  const closeAndDeselect = useCallback(() => {
    closeDialog();
    selectedStore.set(initialSelect);
    onDirectoryChange?.(initialDirectory);
  }, [
    closeDialog,
    selectedStore,
    initialSelect,
    initialDirectory,
    onDirectoryChange,
  ]);

  return (
    <DocumentationBrowserDialog
      isOpen={isOpen}
      onClose={closeAndDeselect}
      titleFallbackId={CommonTranslationKeys.documentation}
      searchDataQa='documentation-browse-dialog-search'
      browser={browser}
      confirmLabelId={CommonTranslationKeys.select}
      onConfirm={onSubmit}
      confirmDisabled={!enableSelect || browser.loading || Boolean(browser.loadError)}
      confirmLoading={browser.loading}
    />
  );
}
